import React, { useState } from "react"
import {
  Modal,
  Card,
  Space,
  Typography,
  MailOutlined,
  TeamOutlined,
} from "@mimber/ui-web"
import { Resource, Action } from "@mimber/schema"
import { useOrganizationPermissions } from "@/core/hooks/useOrganizationPermissions"
import { ImportWizardModal } from "@/components/blocks/ImportWizardModal"
import { managedMemberImportConfig } from "@/config/imports/managedMemberImportConfig"
import { memberInvitationImportConfig } from "@/config/imports/memberInvitationImportConfig"
import type { ImportMembersModalProps } from "./ImportMembersModal.types"

const { Text, Paragraph } = Typography

type ImportMode = "managed" | "invite"

const MODE_TITLES: Record<ImportMode, string> = {
  managed: managedMemberImportConfig.title,
  invite: memberInvitationImportConfig.title,
}

/**
 * Entry point for bulk member import — mirrors CreateMemberModal's mode-card
 * picker (managed vs invite) so the admin decides up front, before the
 * generic upload/mapping wizard opens.
 */
export const ImportMembersModal: React.FunctionComponent<
  ImportMembersModalProps
> = ({ onSuccess, ...modalProps }) => {
  const [mode, setMode] = useState<ImportMode | null>(null)
  const { hasPermission } = useOrganizationPermissions()
  const canAddManaged = hasPermission(Resource.MEMBERSHIP, Action.CREATE)
  const canInvite = hasPermission(Resource.INVITATION, Action.CREATE)

  const handleModeCardKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    nextMode: ImportMode
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      setMode(nextMode)
    }
  }

  return (
    <Modal
      footer={null}
      title={mode ? MODE_TITLES[mode] : "Import Members"}
      afterClose={() => setMode(null)}
      width={mode ? 720 : undefined}
      {...modalProps}
    >
      {(close) => {
        if (!mode) {
          return (
            <Space
              direction="vertical"
              style={{ width: "100%", marginTop: 16 }}
              size="middle"
            >
              {canAddManaged && (
                <Card
                  hoverable
                  role="button"
                  tabIndex={0}
                  onClick={() => setMode("managed")}
                  onKeyDown={(event) => handleModeCardKeyDown(event, "managed")}
                >
                  <Space align="center">
                    <TeamOutlined style={{ fontSize: 28, paddingRight: 8 }} />
                    <div>
                      <Text strong>Import Managed Members</Text>
                      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        No emails are sent. You'll manage these members'
                        information on their behalf.
                      </Paragraph>
                    </div>
                  </Space>
                </Card>
              )}
              {canInvite && (
                <Card
                  hoverable
                  role="button"
                  tabIndex={0}
                  onClick={() => setMode("invite")}
                  onKeyDown={(event) => handleModeCardKeyDown(event, "invite")}
                >
                  <Space align="center">
                    <MailOutlined style={{ fontSize: 28, paddingRight: 8 }} />
                    <div>
                      <Text strong>Bulk Invite Members</Text>
                      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        Send each person an email invitation to create their own
                        account.
                      </Paragraph>
                    </div>
                  </Space>
                </Card>
              )}
            </Space>
          )
        }

        const config =
          mode === "managed"
            ? managedMemberImportConfig
            : memberInvitationImportConfig

        return (
          <ImportWizardModal
            config={config}
            onSuccess={() => {
              close()
              onSuccess?.()
            }}
            onCancel={close}
          />
        )
      }}
    </Modal>
  )
}
